import React from 'react';
import { cn } from '../lib/utils';

export default function MagicBall({ isShaking, currentAnswer, isRevealing, hasAsked }) {
  return (
    <div className="relative">
      {/* Outer glow ring */}
      <div 
        className={cn(
          "absolute inset-0 rounded-full blur-2xl transition-all duration-500",
          isShaking ? "scale-110" : "scale-100"
        )}
        style={{
          background: 'radial-gradient(circle, hsl(265 72% 58% / 0.4) 0%, transparent 70%)',
          width: '350px',
          height: '350px',
          marginLeft: '-25px',
          marginTop: '-25px',
        }}
      />
      
      {/* The Magic 8 Ball */}
      <div
        className={cn(
          "relative w-[300px] h-[300px] rounded-full cursor-pointer",
          "transition-transform duration-200",
          isShaking && "animate-shake",
          !isShaking && "hover:scale-105"
        )}
        style={{
          background: `
            radial-gradient(circle at 30% 30%, hsl(0 0% 25%) 0%, hsl(0 0% 5%) 50%, hsl(0 0% 2%) 100%)
          `,
          boxShadow: `
            0 20px 60px hsl(0 0% 0% / 0.5),
            0 0 80px hsl(265 72% 58% / 0.2),
            inset 0 -20px 60px hsl(0 0% 0% / 0.8),
            inset 0 20px 40px hsl(0 0% 30% / 0.2)
          `,
        }}
      >
        {/* Glossy highlight */}
        <div 
          className="absolute top-4 left-8 w-24 h-16 rounded-full opacity-30"
          style={{
            background: 'linear-gradient(180deg, hsl(0 0% 100% / 0.4) 0%, transparent 100%)',
            filter: 'blur(10px)',
          }}
        />

        {/* The viewing window (blue circle) */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140px] h-[140px] rounded-full overflow-hidden"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                hsl(220 80% 20%) 0%, 
                hsl(230 70% 15%) 50%, 
                hsl(240 60% 10%) 100%
              )
            `,
            boxShadow: `
              inset 0 0 30px hsl(0 0% 0% / 0.8),
              inset 0 0 60px hsl(220 80% 30% / 0.3),
              0 0 20px hsl(0 0% 0% / 0.5)
            `,
          }}
        >
          {/* Inner liquid effect */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: `
                radial-gradient(ellipse at 30% 30%, hsl(185 100% 50% / 0.1) 0%, transparent 50%)
              `,
            }}
          />

          {/* Content inside the window */}
          <div className="absolute inset-0 flex items-center justify-center">
            {!hasAsked ? (
              // Show "8" when no question has been asked
              <span 
                className="text-6xl font-bold select-none"
                style={{
                  color: 'hsl(0 0% 100%)',
                  textShadow: `
                    0 0 20px hsl(185 100% 50% / 0.5),
                    0 0 40px hsl(265 72% 58% / 0.3)
                  `,
                }}
              >
                8
              </span>
            ) : currentAnswer ? (
              // Show answer with triangle
              <div 
                className={cn(
                  "flex flex-col items-center justify-center p-2",
                  isRevealing && "animate-reveal"
                )}
              >
                {/* Triangle shape */}
                <div 
                  className="relative flex items-center justify-center"
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '55px solid transparent',
                    borderRight: '55px solid transparent',
                    borderBottom: `95px solid hsl(220 70% 20%)`,
                  }}
                >
                  {/* Answer text */}
                  <span 
                    className="absolute text-center text-xs font-semibold leading-tight px-1 max-w-[90px]"
                    style={{
                      top: '35px',
                      color: 'hsl(185 100% 70%)',
                      textShadow: '0 0 10px hsl(185 100% 50% / 0.5)',
                    }}
                  >
                    {currentAnswer.text}
                  </span>
                </div>
              </div>
            ) : (
              // Show loading/thinking state
              <div className="flex items-center justify-center">
                <div 
                  className="w-3 h-3 rounded-full animate-pulse"
                  style={{
                    background: 'hsl(185 100% 50%)',
                    boxShadow: '0 0 20px hsl(185 100% 50% / 0.5)',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Subtle reflection on bottom */}
        <div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 w-32 h-8 rounded-full opacity-10"
          style={{
            background: 'linear-gradient(0deg, hsl(0 0% 100% / 0.3) 0%, transparent 100%)',
            filter: 'blur(8px)',
          }}
        />
      </div>

      {/* Shadow beneath the ball */}
      <div 
        className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-48 h-8 rounded-full"
        style={{
          background: 'radial-gradient(ellipse, hsl(0 0% 0% / 0.5) 0%, transparent 70%)',
          filter: 'blur(10px)',
        }}
      />

      {/* Floating particles around the ball */}
      <div className="absolute inset-0 pointer-events-none">
        {[
          { left: '25%', top: '30%' },
          { left: '70%', top: '25%' },
          { left: '35%', top: '65%' },
          { left: '60%', top: '70%' },
          { left: '45%', top: '40%' },
          { left: '55%', top: '55%' },
        ].map((pos, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-primary/60"
            style={{
              left: pos.left,
              top: pos.top,
              animation: `float ${3 + i * 0.5}s ease-in-out ${i * 0.3}s infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
